// Draw — the dedicated draw screen (card d76b3080, Devin 2026-10-19). Draw one
// competition at a time, check the order, then redraw or confirm and lock.
// Shows every dancer's number in order, where each round starts, the dances
// they chose and, for grouped competitions (card 993c6c6c), the dance groups.
// Built to be screen-shared: the ECRO director and ethics person watch the
// draws on a video call. Locking is permanent (decision 2026-07-04); a draft
// can be redrawn as often as needed and the screen says how many times it was.
// The system picks each competition's seed; there is no typed phrase.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import HeaderOrg from '../../Headers/HeaderOrg';
import ToggleSwitch from '../../../Components/ToggleSwitch/ToggleSwitch';
import PublishConfirm, { publishAsk } from '../PublishConfirm';
import Loader from '../../../Components/Loader/Loader';
import { ShowToastMessage } from '../../../helper/ShowToastMessage';
import { serviceConsumer } from '../../../network/ServiceConsumer';
import {
  downloadEventDayPdf,
  generateDraw,
  getDraw,
  getDrawDashboard,
  getDrawsExport,
  lockDraw,
  setNamesListsShown,
} from '../../../network/oireachtasEventDay';
import { Msr, useMaterialSymbols } from '../../OireachtasPanel/symbols';
import {
  STATUS_LABEL,
  choiceColumns,
  choiceText,
  groupLabel,
  groupingTag,
  groupsTogether,
  programList,
  statusOf,
  classmatesTooClose,
  roundOrder,
  viewItems,
} from './drawModel';
import '../../OireachtasPanel/oireachtas.css';
import '../../OireachtasPanel/organizerOireachtas.css';
import { compareComps } from '../../OireachtasPanel/compSort';
import { exportProgramList } from './exportProgramList';
import './drawRoom.css';

const errText = (e, fallback) => e?.error?.response?.data?.message || e?.messages || fallback;

const when = (d) =>
  d
    ? new Date(d).toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

const FILTERS = ['all', 'none', 'draft', 'locked'];

// "26 dancers" / "16 teams": team competitions count teams (the draw orders teams)
const TEAM_TYPES = /^team_|^figure$|^dance_drama$|^adult_team$/;
const entrantLabel = (r) => {
  const n = r.entrantCount || 0;
  const team = TEAM_TYPES.test(String(r.competition?.type || ''));
  return `${n} ${team ? (n === 1 ? 'team' : 'teams') : n === 1 ? 'dancer' : 'dancers'}`;
};

// The draw reveal (Devin 2026-10-19): after Draw / Redraw the numbers fill in
// one dancer at a time, about three seconds in all, then the round starts and
// checks appear. Skipped when the viewer asks for reduced motion.
const REVEAL_TOTAL_MS = 3000;
const reducedMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    return false;
  }
};

const OireachtasDrawRoom = () => {
  useMaterialSymbols();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const eventId = params.get('id');
  const selected = params.get('comp') || '';

  const [event, setEvent] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');

  const [competition, setCompetition] = useState(null);
  const [draw, setDraw] = useState(null);
  const [setNames, setSetNames] = useState({});
  const [view, setView] = useState(0);
  const [busy, setBusy] = useState(false);
  const [drawError, setDrawError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  // rows revealed so far while the draw animates; null = not animating
  const [reveal, setReveal] = useState(null);
  // "Draw all" on the list: { done, total, code } while running; the rows
  // drawn in this run (they pop in) and any that failed (Devin 2026-10-19)
  const [drawAll, setDrawAll] = useState(null);
  const [justDrawn, setJustDrawn] = useState(() => new Set());
  const [drawAllErrors, setDrawAllErrors] = useState({});
  // Lock all (Devin 2026-10-20): typed confirmation, then one at a time
  const [lockAllOpen, setLockAllOpen] = useState(false);
  const [lockAllTyped, setLockAllTyped] = useState('');
  const stopDrawAll = useRef(false);
  // the running-orders switch asks first (PublishConfirm), like every publish
  const [ask, setAsk] = useState(null);

  // competitions in syllabus order (compSort.js, the organizer panel's order)
  const loadDashboard = async () => {
    const res = await getDrawDashboard(eventId);
    setRows((res.rows || []).slice().sort((a, b) => compareComps(a.competition, b.competition)));
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [eventRes] = await Promise.all([
          serviceConsumer('GET', `${process.env.REACT_APP_BASE_URL}/oireachtas/${eventId}`),
          loadDashboard(),
        ]);
        setEvent(eventRes.event);
      } catch (e) {
        ShowToastMessage('error', errText(e, 'Could not load the competitions'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const loadComp = async () => {
    const res = await getDraw(eventId, selected);
    setCompetition(res.competition || null);
    setDraw(res.draw || null);
    setSetNames(res.setNames || {});
  };

  useEffect(() => {
    setCompetition(null);
    setDraw(null);
    setView(0);
    setDrawError('');
    setConfirming(false);
    setTyped('');
    setReveal(null);
    if (!selected) return;
    (async () => {
      try {
        setBusy(true);
        await loadComp();
      } catch (e) {
        ShowToastMessage('error', errText(e, 'Could not load the draw'));
      } finally {
        setBusy(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, selected]);

  const openComp = (compId) => navigate(`/oireachtas/draws?id=${eventId}&comp=${compId}`);
  const toList = () => navigate(`/oireachtas/draws?id=${eventId}`);
  const toManage = () => navigate(`/oireachtas/manage?id=${eventId}`);

  // ---- list ------------------------------------------------------------------
  const counts = useMemo(() => {
    const c = { all: rows.length, none: 0, draft: 0, locked: 0 };
    rows.forEach((r) => (c[statusOf(r)] += 1));
    return c;
  }, [rows]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter !== 'all' && statusOf(r) !== filter) return false;
      if (!q) return true;
      return `${r.competition.code} ${r.competition.name}`.toLowerCase().includes(q);
    });
  }, [rows, query, filter]);

  // Competitions in list order, for Previous / Next on the competition view.
  const index = rows.findIndex((r) => String(r.competition._id) === String(selected));
  const prevRow = index > 0 ? rows[index - 1] : null;
  const nextRow = index >= 0 && index < rows.length - 1 ? rows[index + 1] : null;
  const row = index >= 0 ? rows[index] : null;

  const dancerCount = draw?.order?.length || 0;
  useEffect(() => {
    if (reveal === null) return undefined;
    if (reveal >= dancerCount) {
      setReveal(null);
      return undefined;
    }
    const step = Math.max(35, Math.min(150, REVEAL_TOTAL_MS / Math.max(1, dancerCount)));
    const t = setTimeout(() => setReveal((n) => (n === null ? null : n + 1)), step);
    return () => clearTimeout(t);
  }, [reveal, dancerCount]);
  const revealing = reveal !== null;

  // ---- actions -------------------------------------------------------------
  const doDraw = async () => {
    try {
      setBusy(true);
      setDrawError('');
      setConfirming(false);
      await generateDraw(eventId, selected, '');
      await Promise.all([loadComp(), loadDashboard()]);
      setView(0);
      if (!reducedMotion()) setReveal(0);
    } catch (e) {
      setDrawError(errText(e, 'The draw did not work'));
    } finally {
      setBusy(false);
    }
  };

  const doLock = async () => {
    try {
      setBusy(true);
      await lockDraw(eventId, selected);
      await Promise.all([loadComp(), loadDashboard()]);
      setConfirming(false);
      setTyped('');
      ShowToastMessage('success', `${competition?.code} is locked`);
    } catch (e) {
      ShowToastMessage('error', errText(e, 'Lock failed'));
    } finally {
      setBusy(false);
    }
  };

  const dl = async (path, suffix) => {
    try {
      setBusy(true);
      await downloadEventDayPdf(eventId, selected, path, `${competition?.code || 'comp'}-${suffix}.pdf`);
    } catch (e) {
      ShowToastMessage('error', 'The PDF could not be opened');
    } finally {
      setBusy(false);
    }
  };

  // Draw all (Devin 2026-10-19): every competition not drawn yet, one at a
  // time in syllabus order, each row's round starts filling in as it is drawn
  // so the people watching see it happen. Drafts are left alone (redraw them
  // one at a time); nothing is locked.
  const doDrawAll = async () => {
    const targets = rows.filter((r) => statusOf(r) === 'none' && r.entrantCount > 0);
    if (!targets.length) {
      ShowToastMessage('error', 'Every competition with dancers is already drawn');
      return;
    }
    const go = window.confirm(
      `Draw all ${targets.length} competitions not drawn yet?\n\nThey are drawn one at a time. Nothing is locked: ` +
        'check each one, then confirm and lock it.',
    );
    if (!go) return;
    setFilter('all');
    setQuery('');
    setJustDrawn(new Set());
    setDrawAllErrors({});
    stopDrawAll.current = false;
    const pause = reducedMotion() ? 0 : 350;
    let drawn = 0;
    for (let i = 0; i < targets.length; i++) {
      if (stopDrawAll.current) break;
      const t = targets[i];
      const id = String(t.competition._id);
      setDrawAll({ verb: 'Drawing', done: i, total: targets.length, code: t.competition.code, id });
      const el = document.getElementById(`odr-li-${id}`);
      if (el) el.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
      try {
        const res = await generateDraw(eventId, id, '');
        setRows((prev) => prev.map((r) => (String(r.competition._id) === id ? { ...r, draw: res.draw } : r)));
        setJustDrawn((prev) => new Set(prev).add(id));
        drawn += 1;
      } catch (e) {
        setDrawAllErrors((prev) => ({ ...prev, [id]: errText(e, 'Draw failed') }));
      }
      if (pause) await new Promise((resolve) => setTimeout(resolve, pause));
    }
    setDrawAll(null);
    await loadDashboard();
    ShowToastMessage(
      'success',
      `Drew ${drawn} of ${targets.length} competitions${stopDrawAll.current ? ' (stopped)' : ''}`,
    );
  };

  // Lock all (Devin 2026-10-20): every draft, one at a time in syllabus order,
  // each row switching to Locked as the people watching follow along. Locking
  // is final; competitions not drawn yet are left as they are.
  const LOCK_ALL_WORDS = 'LOCK ALL';
  const doLockAll = async () => {
    const targets = rows.filter((r) => statusOf(r) === 'draft');
    if (!targets.length) return;
    setLockAllOpen(false);
    setLockAllTyped('');
    setFilter('all');
    setQuery('');
    setJustDrawn(new Set());
    setDrawAllErrors({});
    stopDrawAll.current = false;
    const pause = reducedMotion() ? 0 : 200;
    let locked = 0;
    for (let i = 0; i < targets.length; i++) {
      if (stopDrawAll.current) break;
      const t = targets[i];
      const id = String(t.competition._id);
      setDrawAll({ verb: 'Locking', done: i, total: targets.length, code: t.competition.code, id });
      const el = document.getElementById(`odr-li-${id}`);
      if (el) el.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
      try {
        await lockDraw(eventId, id);
        setRows((prev) =>
          prev.map((r) =>
            String(r.competition._id) === id
              ? { ...r, draw: { ...r.draw, locked: true, lockedAt: new Date().toISOString() } }
              : r,
          ),
        );
        locked += 1;
      } catch (e) {
        setDrawAllErrors((prev) => ({ ...prev, [id]: errText(e, 'Lock failed') }));
      }
      if (pause) await new Promise((resolve) => setTimeout(resolve, pause));
    }
    setDrawAll(null);
    await loadDashboard();
    ShowToastMessage(
      'success',
      `Locked ${locked} of ${targets.length} draws${stopDrawAll.current ? ' (stopped)' : ''}`,
    );
  };
  const undrawnWithEntrants = rows.filter((r) => statusOf(r) === 'none' && r.entrantCount > 0);

  // Names only, in round 1 order, for the program and the app (numbers are
  // released the morning of the event).
  const doProgramList = async () => {
    try {
      setBusy(true);
      const res = await getDrawsExport(eventId);
      // same syllabus order as the list on this screen (the export sorts by group and code)
      const pos = Object.fromEntries(rows.map((r, i) => [String(r.competition._id), i]));
      const at = (r) => pos[String(r.competition._id)] ?? rows.length;
      const ordered = (res.rows || []).slice().sort((a, b) => at(a) - at(b));
      const { rows: list, notLocked } = programList(ordered);
      if (!list.length) {
        ShowToastMessage('error', 'No locked draws yet');
        return;
      }
      exportProgramList(list, `${event?.name || 'Oireachtas'} - program list.csv`);
      if (notLocked.length) {
        ShowToastMessage('success', `Program list saved. Not included (not locked yet): ${notLocked.join(', ')}`);
      }
    } catch (e) {
      ShowToastMessage('error', errText(e, 'Program list failed'));
    } finally {
      setBusy(false);
    }
  };

  // ---- competition view ------------------------------------------------------
  const rounds = Number(draw?.rounds || competition?.rounds) || 1;
  const cols = choiceColumns(competition, draw);
  // number, dancer, school, then one column per chosen dance. Built here: CSS
  // repeat(0, ...) is invalid and collapsed the table when there were none.
  const tableColumns = ['76px', 'minmax(170px, 1.3fr)', 'minmax(150px, 1fr)']
    .concat(cols.map(() => 'minmax(150px, 1fr)'))
    .join(' ');
  const items = viewItems(draw, view);
  // while revealing: a row shows once its turn comes; a marker or heading
  // shows with the row after it
  const itemShown = (() => {
    if (reveal === null) return items.map(() => true);
    const shownFlags = new Array(items.length).fill(false);
    let rowsBefore = 0;
    items.forEach((it, i) => {
      if (it.kind === 'row') {
        shownFlags[i] = rowsBefore < reveal;
        rowsBefore += 1;
      } else {
        shownFlags[i] = rowsBefore < reveal;
      }
    });
    return shownFlags;
  })();
  // checked on round 1 as danced: a circle when later rounds wrap, else a line
  const tooClose = draw ? classmatesTooClose(roundOrder(draw, 1), (Number(draw.rounds) || 1) > 1) : [];
  const grouping = draw?.grouping || null;
  const numbers = draw?.order?.length ? `${draw.order[0].number}–${draw.order[draw.order.length - 1].number}` : '';
  const tag = groupingTag(draw?.grouping || competition?.grouping);
  const status = draw ? (draw.locked ? 'locked' : 'draft') : 'none';

  // Running orders for families and teachers (card f6c2c874, Devin
  // 2026-10-25): names only, shown when Devin switches it on (a day or two
  // before, at will). The 7:00 am numbers release doesn't wait for it.
  const namesOn = event?.namesListsShown === true;
  const toggleNamesLists = async () => {
    try {
      setBusy(true);
      const res = await setNamesListsShown(eventId, !namesOn);
      setEvent((prev) => ({ ...prev, namesListsShown: res.namesListsShown === true }));
      ShowToastMessage('success', res.message || 'Running orders updated');
    } catch (e) {
      ShowToastMessage('error', errText(e, 'Could not change the running orders switch'));
    } finally {
      setBusy(false);
    }
  };

  const renderList = () => (
    <>
      <div className="odr-ph">
        <div>
          <h1 className="odr-h1">Draw</h1>
          <div className="odr-sub">{event?.name || ' '}</div>
        </div>
        <div className="odr-ph-acts">
          <button
            type="button"
            className="op-btn"
            disabled={busy || !!drawAll || !counts.locked}
            onClick={doProgramList}
            title="Names in round 1 order, without numbers, for the program"
          >
            <Msr>download</Msr>Program list
          </button>
          <button
            type="button"
            className="odr-btn primary"
            disabled={busy || !!drawAll || !counts.none}
            onClick={doDrawAll}
          >
            <Msr>casino</Msr>Draw all
          </button>
          <button
            type="button"
            className="odr-btn"
            disabled={busy || !!drawAll || !counts.draft}
            onClick={() => {
              setLockAllOpen(true);
              setLockAllTyped('');
            }}
          >
            <Msr>lock</Msr>Lock all
          </button>
        </div>
      </div>
      {event && (
        <div className={`odr-switch${namesOn ? ' on' : ''}`}>
          <ToggleSwitch
            checked={namesOn}
            disabled={busy || !!drawAll}
            onChange={() => setAsk({ ...publishAsk('names', '', !namesOn), run: toggleNamesLists })}
          />
          <div>
            <div className="odr-switch-t">Running orders for families and teachers</div>
            <div className="odr-switch-s">
              {namesOn
                ? 'Showing on feis.link and in the app: names only, in each locked draw’s order. Numbers come out at 7:00 am on each competition’s day.'
                : 'Hidden until you switch this on. Each competition’s numbered running order still comes out at 7:00 am on its day.'}
            </div>
          </div>
        </div>
      )}
      {lockAllOpen && !drawAll && (
        <div className="odr-confirm" role="group" aria-label="Lock all">
          <div className="odr-confirm-t">
            Lock all {counts.draft} draft {counts.draft === 1 ? 'draw' : 'draws'}? This is final.
          </div>
          <div className="odr-confirm-s">
            Their order and numbers become permanent and can&apos;t be redrawn.
            {undrawnWithEntrants.length > 0 && (
              <>
                {' '}
                Not drawn yet, so not locked: <b>{undrawnWithEntrants.map((r) => r.competition.code).join(', ')}</b>.
              </>
            )}{' '}
            Type <b>{LOCK_ALL_WORDS}</b> to confirm.
          </div>
          <div className="odr-confirm-row">
            <input
              type="text"
              value={lockAllTyped}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              aria-label={`Type ${LOCK_ALL_WORDS} to confirm`}
              onChange={(e) => setLockAllTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && lockAllTyped.trim().toUpperCase() === LOCK_ALL_WORDS) doLockAll();
              }}
            />
            <button
              type="button"
              className="odr-btn primary"
              disabled={lockAllTyped.trim().toUpperCase() !== LOCK_ALL_WORDS}
              onClick={doLockAll}
            >
              <Msr>lock</Msr>Lock all
            </button>
            <button type="button" className="odr-btn" onClick={() => setLockAllOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {drawAll && (
        <div className="odr-drawall" role="status">
          <div className="odr-drawall-t">
            {drawAll.verb} <b>{drawAll.code}</b> · {drawAll.done + 1} of {drawAll.total}
          </div>
          <div className="odr-drawall-bar" aria-hidden="true">
            <span style={{ width: `${Math.round(((drawAll.done + 1) / drawAll.total) * 100)}%` }} />
          </div>
          <button type="button" className="op-btn sm" onClick={() => (stopDrawAll.current = true)}>
            Stop
          </button>
        </div>
      )}
      <p className="odr-lead">
        Draw each competition, check the order, then confirm and lock it. A draft can be redrawn; a locked draw is
        final.
      </p>
      <div className="odr-bar">
        <div className="odr-chips" role="tablist" aria-label="Show">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              role="tab"
              aria-selected={filter === f}
              className={`odr-chip${filter === f ? ' on' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'All' : STATUS_LABEL[f]} <span className="n">{counts[f]}</span>
            </button>
          ))}
        </div>
        <label className="odr-search">
          <Msr>search</Msr>
          <input
            type="text"
            value={query}
            placeholder="Code or name"
            aria-label="Find a competition"
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </div>
      <div className="odr-list card">
        <div className="odr-lh" aria-hidden="true">
          <span>Code</span>
          <span>Competition</span>
          <span>Dancers</span>
          <span className="c">R1</span>
          <span className="c">R2</span>
          <span className="c">R3</span>
          <span>Status</span>
          <span />
        </div>
        {shown.map((r) => {
          const st = statusOf(r);
          const id = String(r.competition._id);
          const starts = r.draw?.roundStarts || [];
          const rowClass = [
            'odr-li',
            drawAll?.id === id ? 'drawing' : '',
            justDrawn.has(id) && !reducedMotion() ? 'popped' : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <button
              key={r.competition._id}
              id={`odr-li-${id}`}
              type="button"
              className={rowClass}
              disabled={!!drawAll}
              onClick={() => openComp(r.competition._id)}
            >
              <span className="odr-code">{r.competition.code}</span>
              <span className="odr-name">
                {r.competition.name}
                {r.grouping && <span className="odr-tag">{groupingTag(r.grouping)}</span>}
              </span>
              <span className="odr-count">{entrantLabel(r)}</span>
              {[0, 1, 2].map((k) => (
                <span key={k} className={`odr-li-r r${k + 1}`} aria-label={`Round ${k + 1} starts`}>
                  {starts[k] != null ? starts[k] : ''}
                </span>
              ))}
              <span className={`odr-pill ${st}`}>
                {STATUS_LABEL[st]}
                {st === 'draft' && r.draw?.drawCount > 1 ? ` · drawn ${r.draw.drawCount}×` : ''}
              </span>
              {drawAllErrors[id] && <span className="odr-li-err">{drawAllErrors[id]}</span>}
              <Msr>chevron_right</Msr>
            </button>
          );
        })}
        {!loading && !shown.length && <div className="odr-empty">No competitions match.</div>}
      </div>
    </>
  );

  const renderComp = () => (
    <>
      <div className="odr-nav">
        <button type="button" className="op-back" onClick={toList}>
          <Msr>arrow_back</Msr>All competitions
        </button>
        <div className="odr-step">
          <button
            type="button"
            className="op-btn sm"
            disabled={!prevRow || busy}
            onClick={() => openComp(prevRow.competition._id)}
          >
            <Msr>chevron_left</Msr>
            {prevRow ? prevRow.competition.code : 'Previous'}
          </button>
          <button
            type="button"
            className="op-btn sm"
            disabled={!nextRow || busy}
            onClick={() => openComp(nextRow.competition._id)}
          >
            {nextRow ? nextRow.competition.code : 'Next'}
            <Msr>chevron_right</Msr>
          </button>
        </div>
      </div>

      <div className="odr-ph">
        <div>
          <h1 className="odr-h1">
            <span className="odr-h1-code">{competition?.code || row?.competition?.code}</span>
            {competition?.name || row?.competition?.name}
          </h1>
          <div className="odr-sub">
            {[row ? entrantLabel(row) : '', rounds > 1 ? `${rounds} rounds` : '1 round', tag]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
        <span className={`odr-pill big ${status}`}>{STATUS_LABEL[status]}</span>
      </div>

      {draw && (
        <div className={`odr-starts${revealing ? ' waiting' : ' shown'}`} aria-label="Round start numbers">
          {(draw.roundStarts || []).map((n, i) => (
            <div key={i} className="odr-start" style={{ '--i': i }}>
              <span>Round {i + 1} starts</span>
              <b>{revealing ? '–' : n}</b>
            </div>
          ))}
        </div>
      )}

      {drawError && (
        <div className="op-error" role="alert">
          {drawError}
        </div>
      )}

      <div className="odr-actions">
        {status === 'none' && (
          <button type="button" className="odr-btn primary" disabled={busy} onClick={doDraw}>
            <Msr>casino</Msr>Draw this competition
          </button>
        )}
        {status === 'draft' && !confirming && (
          <>
            <button type="button" className="odr-btn" disabled={busy || revealing} onClick={doDraw}>
              <Msr>refresh</Msr>Redraw
            </button>
            <button
              type="button"
              className="odr-btn primary"
              disabled={busy || revealing}
              onClick={() => {
                setConfirming(true);
                setTyped('');
              }}
            >
              <Msr>lock</Msr>Confirm and lock
            </button>
          </>
        )}
        {status === 'locked' && (
          <>
            <span className="odr-locked">
              <Msr>lock</Msr>Locked {when(draw.lockedAt)}
            </span>
            <button type="button" className="op-btn sm" disabled={busy} onClick={() => dl('cards.pdf', 'cards')}>
              <Msr>print</Msr>Number cards
            </button>
            <button
              type="button"
              className="op-btn sm"
              disabled={busy}
              onClick={() => dl('checkin.pdf?round=1', 'checkin')}
            >
              <Msr>print</Msr>Check-in sheet
            </button>
            <button
              type="button"
              className="op-btn sm"
              disabled={busy}
              onClick={() => dl('scoresheets.pdf', 'scoresheets')}
            >
              <Msr>print</Msr>Score sheets
            </button>
          </>
        )}
      </div>

      {confirming && status === 'draft' && (
        <div className="odr-confirm" role="group" aria-label="Confirm and lock">
          <div className="odr-confirm-t">Lock {competition?.code}? This is final.</div>
          <div className="odr-confirm-s">
            The order and numbers below become permanent and can&apos;t be redrawn. Type <b>{competition?.code}</b> to
            confirm.
          </div>
          <div className="odr-confirm-row">
            <input
              type="text"
              value={typed}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              aria-label={`Type ${competition?.code} to confirm`}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && typed.trim().toUpperCase() === String(competition?.code).toUpperCase())
                  doLock();
              }}
            />
            <button
              type="button"
              className="odr-btn primary"
              disabled={busy || typed.trim().toUpperCase() !== String(competition?.code).toUpperCase()}
              onClick={doLock}
            >
              <Msr>lock</Msr>Lock
            </button>
            <button type="button" className="odr-btn" disabled={busy} onClick={() => setConfirming(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {draw && revealing && (
        <div className="odr-revealing" role="status">
          <span>
            Drawing… {Math.min(reveal, dancerCount)} of {dancerCount}
          </span>
          <button type="button" className="op-btn sm" onClick={() => setReveal(null)}>
            Show all
          </button>
        </div>
      )}

      {draw && (
        <>
          {!revealing && (
            <>
              <div className="odr-meta">
                <span>
                  Drawn <b>{draw.drawCount || 1}</b> {(draw.drawCount || 1) === 1 ? 'time' : 'times'}, last{' '}
                  {when(draw.drawnAt)}
                </span>
                {numbers && (
                  <span>
                    Numbers <b>{numbers}</b>
                  </span>
                )}
                {rounds > 1 && (
                  <span>
                    Rounds start at <b>{(draw.roundStarts || []).join(', ')}</b>
                  </span>
                )}
                <span className="odr-seed" title="The system-picked seed this draw came from">
                  Seed {draw.seed}
                </span>
              </div>

              <div className="odr-checks">
                {tooClose.length === 0 ? (
                  <div className="odr-check ok">
                    <Msr>check_circle</Msr>Classmates at least two dancers apart in every round
                  </div>
                ) : (
                  <div className="odr-check warn">
                    <Msr>warning</Msr>
                    {tooClose.length === 1 ? 'One pair of classmates' : `${tooClose.length} pairs of classmates`} closer
                    than two dancers apart (the draw couldn&apos;t separate them further):{' '}
                    {tooClose
                      .map(
                        ([a, b, apart]) =>
                          `#${a.number} and #${b.number} (${apart === 1 ? 'side by side' : '1 between'}, ${a.schoolName || 'same school'})`,
                      )
                      .join('; ')}
                  </div>
                )}
                {grouping && (
                  <div className={`odr-check ${groupsTogether(draw) ? 'ok' : 'warn'}`}>
                    <Msr>{groupsTogether(draw) ? 'check_circle' : 'warning'}</Msr>
                    {grouping.by === 'roundDance' ? `Round ${grouping.round}: ` : ''}
                    {(grouping.groups || []).map((k) => groupLabel(grouping, k, setNames)).join(', then ')}
                    {groupsTogether(draw) ? ', each danced together' : ' are NOT together — redraw'}
                  </div>
                )}
              </div>

              <div className="odr-chips odr-views" role="tablist" aria-label="Order shown">
                {[0, ...Array.from({ length: rounds }, (_, i) => i + 1)].map((v) => (
                  <button
                    key={v}
                    type="button"
                    role="tab"
                    aria-selected={view === v}
                    className={`odr-chip${view === v ? ' on' : ''}`}
                    onClick={() => setView(v)}
                  >
                    {v === 0 ? 'Draw order' : `Round ${v} as danced`}
                  </button>
                ))}
              </div>
            </>
          )}

          <div className="odr-table card" role="table" aria-label={`${competition?.code} draw`}>
            <div className="odr-tr odr-th" role="row" style={{ gridTemplateColumns: tableColumns }}>
              <span role="columnheader">#</span>
              <span role="columnheader">{draw?.entryType === 'team' ? 'Team' : 'Dancer'}</span>
              <span role="columnheader">School</span>
              {cols.map((c) => (
                <span key={c.key} role="columnheader">
                  {c.label}
                </span>
              ))}
            </div>
            {items.map((it, i) => {
              if (!itemShown[i]) return null;
              if (it.kind === 'start') {
                return (
                  <div key={`s${it.round}-${i}`} className="odr-marker" role="row">
                    <span role="cell">Round {it.round} starts here</span>
                  </div>
                );
              }
              if (it.kind === 'group') {
                return (
                  <div key={`g${it.key}-${i}`} className="odr-group" role="row">
                    <span role="cell">{groupLabel(grouping, it.key, setNames)}</span>
                  </div>
                );
              }
              const r = it.row;
              return (
                <div
                  key={r.ref}
                  className={`odr-tr${revealing ? ' odr-pop' : ''}`}
                  role="row"
                  style={{ gridTemplateColumns: tableColumns }}
                >
                  <span role="cell" className="odr-num">
                    {r.number}
                  </span>
                  <span role="cell" className="odr-dn">
                    {r.label}
                  </span>
                  <span role="cell" className="odr-sc">
                    {r.schoolName}
                  </span>
                  {cols.map((c) => (
                    <span key={c.key} role="cell" className="odr-ch">
                      {choiceText(c, r, setNames)}
                    </span>
                  ))}
                </div>
              );
            })}
          </div>
        </>
      )}

      {!draw && !busy && competition && (
        <div className="odr-empty card">Not drawn yet. Press “Draw this competition” to draw it.</div>
      )}
    </>
  );

  return (
    <>
      <HeaderOrg />
      <div className="oir op-page odr-page">
        {(loading || busy) && <Loader />}
        <div className="wrap">
          {!selected && (
            <button type="button" className="op-back" onClick={toManage}>
              <Msr>arrow_back</Msr>Manage Oireachtas
            </button>
          )}
          {selected ? renderComp() : renderList()}
        </div>
      </div>
      <PublishConfirm
        ask={ask}
        onConfirm={() => {
          const run = ask?.run;
          setAsk(null);
          if (run) run();
        }}
        onCancel={() => setAsk(null)}
      />
      <ToastContainer />
    </>
  );
};

export default OireachtasDrawRoom;
